import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import analytics from "@/utils/analytics";

import { hoverLiftCard } from "@/utils/motionVariants";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const STUDY_WEBSITES = {
  "cavern-club": "www.cavernclub.com",
  "gladstones-library": "www.gladstoneslibrary.org",
  "krcs": "www.krcs.co.uk",
  "thorncliffe": "www.thorncliffebs.co.uk",
};

const STUDIES = [
  {
    slug: "cavern-club",
    name: "The Cavern Club",
    industry: "Live Music & Hospitality",
    img: "https://cdn.candopayments.com/case-studies/cavern-club_hero_colour_web_solid.jpeg",
    logo: "https://cdn.candopayments.com/clients/cavern-club_colour_web_solid.png",
    summary: "The most famous club in the world — live music 365 days a year across 5 venue spaces. CanDo Payments powers every bar, restaurant, and retail transaction.",
  },
  {
    slug: "gladstones-library",
    name: "Gladstone's Library",
    industry: "Heritage, Hospitality & Culture",
    img: "https://cdn.candopayments.com/case-studies/gladstones-library_hero_colour_web_solid.jpeg",
    logo: null,
    summary: "The UK's only Prime Ministerial library — 26 bedrooms, an award-winning restaurant, and world-famous Reading Rooms. Unified payments across every touchpoint.",
  },
  {
    slug: "krcs",
    name: "KRCS",
    industry: "Premium Retail & E-Commerce",
    img: "https://cdn.candopayments.com/case-studies/krcs_hero_colour_web_solid.webp",
    logo: null,
    summary: "Apple Premium Reseller since 1984 — handling high-value transactions across retail stores and e-commerce with the speed and security Apple customers expect.",
  },
  {
    slug: "thorncliffe",
    name: "Thorncliffe Building Supplies",
    industry: "Building Supplies & Trade",
    img: "https://cdn.candopayments.com/case-studies/thorncliffe_colour_web_solid.jpg",
    logo: null,
    summary: "North Wales' largest independent builder's merchant — 5+ branches, online shop, skip hire, and plant hire. Modernised payments across every operation.",
  },
];

export default function CaseStudiesPage() {
  return (
    <div>
      <SEOHead title="Business Success Stories & Case Studies" description="Read our collection of client success stories and case studies. See how CanDo Payments has empowered various businesses to scale with better technology." path="/case-studies" />
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Case Studies</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Real businesses, <span className="gradient-text">real results</span></motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">See how we've helped businesses across the UK save money, modernise their payments and get the support they deserve.</motion.p>
          </motion.div>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {STUDIES.map((s) => (
              <motion.div key={s.slug} whileHover={hoverLiftCard}>
              <Link to={`/case-studies/${s.slug}`} className="group block bg-white border border-brand-light-grey rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all" data-testid={`cs-link-${s.slug}`}>
                <div className="aspect-[16/9] overflow-hidden relative bg-brand-black">
                  <img src={s.img} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90 group-hover:opacity-100" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                    <span className="bg-white/90 backdrop-blur-sm text-brand-black text-xs font-bold px-3 py-1.5 rounded-full">{s.industry}</span>
                    <button data-measurement-owned="navigation" onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const destination = `https://${STUDY_WEBSITES[s.slug]}`;
                      analytics.clickNavigation("page_content", destination, "outbound");
                      window.open(destination, "_blank");
                    }} className="bg-white/90 backdrop-blur-sm text-brand-grey text-[10px] font-semibold px-2.5 py-1.5 rounded-full hover:text-brand-aqua transition-colors cursor-pointer">
                      Visit website
                    </button>
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="font-heading text-xl font-bold text-brand-black mb-2 group-hover:text-brand-aqua transition-colors">{s.name}</h3>
                  <p className="text-sm text-brand-grey leading-relaxed mb-4">{s.summary}</p>
                  <span className="text-sm font-semibold text-brand-aqua flex items-center gap-1 group-hover:gap-2 transition-all">Read case study <ArrowRight size={14} /></span>
                </div>
              </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
